import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, Briefcase, AlertTriangle, TrendingUp, Crown } from 'lucide-react';
import { LimitMeter } from '@/components/LimitMeter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SeatsUsageIndicator - Shows subscription seat usage with a visual progress bar
 *
 * EIP-224: employees and content team share ONE seat pool. Tenant-facing callers
 * use type='seats' (combined). The 'content_team' / 'employees' types are kept
 * for the superadmin breakdown only.
 *
 * @param {string} type - 'seats' | 'content_team' | 'employees' | 'aggregate'
 * @param {string} variant - 'card' | 'inline' | 'compact'
 * @param {number} refreshKey - Increment to trigger a re-fetch of usage data
 * @param {function} onUsageChange - Callback when usage data is fetched
 * @param {ref} ref - Use ref.current.refresh() to manually trigger a refresh
 */
export const SeatsUsageIndicator = forwardRef(function SeatsUsageIndicator(
  { type = 'aggregate', variant = 'card', className = '', refreshKey = 0, onUsageChange, caption, explanation },
  ref
) {
  const { token } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsage = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/settings/subscription/usage`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
        if (onUsageChange) {
          onUsageChange(data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription usage:', error);
    } finally {
      setLoading(false);
    }
  }, [token, onUsageChange]);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchUsage,
  }), [fetchUsage]);

  // Fetch on mount and when token or refreshKey changes
  useEffect(() => {
    fetchUsage();
  }, [token, refreshKey, fetchUsage]);

  if (loading || !usage) {
    return null;
  }

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const getStatusBadge = (data) => {
    if (data.at_limit) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-200">
          <AlertTriangle className="w-3 h-3 mr-1" />
          At Limit
        </Badge>
      );
    }
    if (data.usage_percentage >= 80) {
      return (
        <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200">
          <TrendingUp className="w-3 h-3 mr-1" />
          Near Limit
        </Badge>
      );
    }
    return null;
  };

  // Render based on type
  if (type === 'seats') {
    const data = usage.seats;
    const percentage = data.unlimited ? 0 : (data.usage_percentage || 0);

    if (variant === 'compact') {
      return (
        <div className={`flex items-center gap-2 ${className}`} data-testid="seats-usage-compact">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {data.current}/{data.unlimited ? '∞' : data.limit}
          </span>
          {!data.unlimited && (
            <Progress
              value={percentage}
              className="w-20 h-2"
              indicatorClassName={getProgressColor(percentage)}
            />
          )}
          {data.unlimited && (
            <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-200">
              <Crown className="w-3 h-3 mr-1" />
              Unlimited
            </Badge>
          )}
        </div>
      );
    }

    return (
      <LimitMeter
        title="Seats"
        used={data.current}
        limit={data.unlimited ? null : data.limit}
        icon={Users}
        displayType="capacity"
        caption={caption}
        explanation={explanation || "Employees and content team share one seat pool."}
        showManagePlanCta
        className={className}
        testId="seats-usage-card"
      />
    );
  }

  if (type === 'content_team') {
    const data = usage.content_team;
    const percentage = data.unlimited ? 0 : (data.usage_percentage || 0);
    
    if (variant === 'compact') {
      return (
        <div className={`flex items-center gap-2 ${className}`} data-testid="content-team-usage-compact">
          <Users className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {data.current}/{data.unlimited ? '∞' : data.limit}
          </span>
          {!data.unlimited && (
            <Progress 
              value={percentage} 
              className="w-20 h-2" 
              indicatorClassName={getProgressColor(percentage)}
            />
          )}
          {data.unlimited && (
            <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-200">
              <Crown className="w-3 h-3 mr-1" />
              Unlimited
            </Badge>
          )}
        </div>
      );
    }

    return (
      <LimitMeter
        title="Content Team Seats"
        used={data.current}
        limit={data.unlimited ? null : data.limit}
        icon={Users}
        displayType="capacity"
        caption={caption}
        explanation={explanation || "Content team members use content team seats, not employee seats."}
        showManagePlanCta
        className={className}
        testId="content-team-usage-card"
      />
    );
  }

  if (type === 'employees') {
    const data = usage.employees;
    const percentage = data.unlimited ? 0 : (data.usage_percentage || 0);
    
    if (variant === 'compact') {
      return (
        <div className={`flex items-center gap-2 ${className}`} data-testid="employee-usage-compact">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium">
            {data.current}/{data.unlimited ? '∞' : data.limit}
          </span>
          {!data.unlimited && (
            <Progress 
              value={percentage} 
              className="w-20 h-2" 
              indicatorClassName={getProgressColor(percentage)}
            />
          )}
          {data.unlimited && (
            <Badge variant="outline" className="bg-purple-500/10 text-purple-600 border-purple-200">
              <Crown className="w-3 h-3 mr-1" />
              Unlimited
            </Badge>
          )}
        </div>
      );
    }

    return (
      <LimitMeter
        title="Employee Seats"
        used={data.current}
        limit={data.unlimited ? null : data.limit}
        icon={Briefcase}
        displayType="capacity"
        caption={caption}
        explanation={explanation}
        showManagePlanCta
        className={className}
        testId="employee-usage-card"
      />
    );
  }

  // Aggregate view (for Billing page). EIP-224: employees and content team share
  // one seat pool, so this shows a single combined "Seats" bar.
  const seats = usage.seats;
  const seatsPercentage = seats.unlimited ? 0 : (seats.usage_percentage || 0);

  return (
    <Card className={className} data-testid="aggregate-usage-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-4">
          <h3 className="text-sm font-semibold text-foreground">Subscription Usage</h3>
          <Badge variant="outline" className="text-xs">
            {usage.subscription_status === 'trial' ? 'Trial' : (usage.pricing_tier || 'Starter')} Plan
          </Badge>
        </div>

        <div className="space-y-4">
          {/* Seats (employees + content team share one pool) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-medium">Seats</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">
                  {seats.current}/{seats.unlimited ? '∞' : seats.limit}
                </span>
                {getStatusBadge(seats)}
              </div>
            </div>
            {!seats.unlimited && (
              <Progress
                value={seatsPercentage}
                className="h-2"
                indicatorClassName={getProgressColor(seatsPercentage)}
              />
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Employees and content team share one seat pool.
            </p>
          </div>
        </div>

        <div className="mt-4 pt-3 border-t border-border">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Total users: {usage.total_users}</span>
            <span>Status: {usage.subscription_status}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default SeatsUsageIndicator;

// Also provide a hook for components that want to trigger usage refresh
export function useSeatsUsageRefresh() {
  const [refreshKey, setRefreshKey] = useState(0);
  
  const triggerRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);
  
  return { refreshKey, triggerRefresh };
}
